
import { defineComponent, SetupContext, PropType, ref, computed, onMounted } from "vue";
import { FridgeDetailsDoorOpenInfoWeeklyItem, FridgeDetailsDoorMonitorWeeklyItemCautionStyle } from "@/types/fridgeDetails"
import NativeClient from "@/client/native-client";
import { translateYYYYMMDDToMMDD } from "@/utils/common-utils";
import { createYAxisScales } from "@/utils/fridgeDetails-utils";

export default defineComponent({
  name: "FridgeDoorMonitorWeeklyItem",
  props: {
    /** ドア開閉情報 */
    list: {
      type: Array as PropType<FridgeDetailsDoorOpenInfoWeeklyItem[]>,
      default: []
    }
  },
  setup(props, context: SetupContext) {
    NativeClient.sendDebugLog("FridgeDoorMonitorDailyItem setup start");

    const noticeText = "ドアが5分以上開いていた場合に表示されます";
    const descriptionText = "・本サービスは監視サービスではありません\n・開閉検知対象のドアは機種により異なります\n・グラフへの反映は少し時間がかかります\n・詳細は、アプリ取説をご確認ください"

    // グラフのスケール
    const yAxisTitle = "(回)"
    const xAxisScales = computed(() => {
      const result: Array<string> = [];
      for (const item of props.list) {
        const dateString = translateYYYYMMDDToMMDD(item.date);
        result.push(dateString);
      }
      for (let i = props.list.length; i < 8; i ++) {
        result.push("");
      }
      return result;
    });
    const xAxisTitle = "(月/日)";
    const baseCanvasSize = { width: 320, height: 240 };
    const canvasSize = computed(() => {
      const scale = window.devicePixelRatio;
      NativeClient.sendDebugLog("FridgeDoorMonitorWeeklyItem devicePixelRatio = " + scale);
      return { width: baseCanvasSize.width * scale, height: baseCanvasSize.height * scale };
    });
    const calcMaxValue = () => {
      // 回数のMax値を求める
      let maxValue = 0;
      if (props.list.length > 0) {
        maxValue = props.list.map(element => element.open_count).reduce((a, b) => Math.max(a, b), -Infinity);
        if (maxValue > 600) {
          // 600超えるならば。600にしておく
          maxValue = 600;
        }
      }
      return maxValue;
    }
    const calsYAxisScales = () => {
      if (props.list.length > 0) {
        const maxValue = calcMaxValue();
        const retValue = createYAxisScales(maxValue);
        NativeClient.sendDebugLog("FridgeDoorMonitorWeeklyItem calcMaxYAxisScales maxValue = " + maxValue + ", scales = " + JSON.stringify(retValue));
        return retValue;
      } 
      return [];
    };
    const yAxisScales = computed(() => {
      const result = calsYAxisScales();
      return result;
    });

    // グラフ描画
    const canvas = ref<HTMLCanvasElement>();
    const drawGraph = () => {
      NativeClient.sendDebugLog("FridgeDoorMonitorWeeklyItem drawGraph start");
      // 実行判定
      if (props.list.length == 0) {
        NativeClient.sendDebugLog("FridgeDoorMonitorWeeklyItem drawGraph list no element");
        return;
      }
      const scales = calsYAxisScales();
      const maxValue = Math.max(...scales);
      if (maxValue <= 0) {
        // 配列無なので描画できない
        NativeClient.sendDebugLog("FridgeDoorMonitorWeeklyItem no maxValue");
        return;
      }
      if (canvas.value === undefined) {
        NativeClient.sendDebugLog("FridgeDoorMonitorWeeklyItem drawGraph canvas.value is undefined");
        return;
      }
      const targetContext = canvas.value.getContext("2d");
      if (targetContext == null) {
        NativeClient.sendDebugLog("FridgeDoorMonitorWeeklyItem drawGraph targetContext is null");
        return;
      }
      const screenScale = window.devicePixelRatio;
      NativeClient.sendDebugLog("FridgeDoorMonitorWeeklyItem drawGraph = " + screenScale);
      const realCanvasSize = { width: baseCanvasSize.width * screenScale, height: baseCanvasSize.height * screenScale };
      targetContext.clearRect(0, 0, realCanvasSize.width, realCanvasSize.height);

      // 定数設定
      const originPosition = { x: 20 * screenScale, y: realCanvasSize.height - 16 * screenScale };
      const barWidth = 16.4 * screenScale;
      const startXAxis = 16 * screenScale + originPosition.x;
      const intervalXAxis = 16.6 * screenScale;
      const maxBarHeight = 205 * screenScale;
      const strokeColor = "#707070";
      const barFillStyle = "rgba(93,214,202,1.0)";
      const linwWidth = screenScale;

      // Y軸
      targetContext.beginPath();
      targetContext.moveTo(originPosition.x, originPosition.y);
      targetContext.lineTo(originPosition.x, 0);
      targetContext.strokeStyle = strokeColor;
      targetContext.lineWidth = linwWidth;
      targetContext.stroke();
      NativeClient.sendDebugLog("FridgeDoorMonitorWeeklyItem drawGraph Y axis start = (" + originPosition.x + ", " + originPosition.y + "), end = (" + originPosition.x + ", " + 0 + ")");

      // X軸
      targetContext.beginPath();
      targetContext.moveTo(originPosition.x, originPosition.y);
      targetContext.lineTo(realCanvasSize.width - 20 * screenScale, originPosition.y);
      targetContext.strokeStyle = strokeColor;
      targetContext.lineWidth = linwWidth;
      targetContext.stroke();

      // 描画
      let currentXPosition = startXAxis;
      for (const item of props.list) {
        let drawHeight = (item.open_count / maxValue) * maxBarHeight;
        if (drawHeight > maxBarHeight) {
          drawHeight = maxBarHeight;
        }
        targetContext.fillStyle = barFillStyle;
        targetContext.fillRect(currentXPosition, originPosition.y - drawHeight, barWidth, drawHeight);

        // 次の位置へ
        currentXPosition = currentXPosition + barWidth + intervalXAxis;
      }
    };

    // 画面が小さい場合の調整
    const styleGraphArea = computed(() => {
      if (window.innerWidth <= 320) {
        return { 'transform': 'scale(0.9)' };
      }
      return { 'transform': 'scale(1.0)' };
    });

    onMounted(() => {
      drawGraph();
    });

    // ボタン押下
    const clickCaution = () => {
      NativeClient.sendDebugLog("FridgeDoorMonitorWeeklyItem clickCaution");
      context.emit("click-caution");
    };

    // ドアアラーム
    const cautionStyles = computed(() => {
      const result: Array<FridgeDetailsDoorMonitorWeeklyItemCautionStyle> = [];
      const scales = calsYAxisScales();
      const maxValue = Math.max(...scales);
      for (const item of props.list) {
        const styleElement = new FridgeDetailsDoorMonitorWeeklyItemCautionStyle(maxValue, item.open_count, item.open_alert, 20.5, 1.2, 20);
        result.push(styleElement);
      }
      return result;
    });

    return {
      styleGraphArea,
      canvas,
      canvasSize,
      yAxisTitle,
      xAxisTitle,
      yAxisScales,
      xAxisScales,
      noticeText,
      descriptionText,
      clickCaution,
      cautionStyles
    };
}
});
